import { Component, Input, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import {
  EngineCampaignAssociationService,
  PersonalCampaignAssociation
} from '../../../../services/engine-campaign-association.service';

@Component({
    selector: 'dmp-dyn-raw-detail-campaigns',
    templateUrl: './dyn-raw-detail-campaigns.component.html',
    styleUrls: ['./dyn-raw-detail-campaigns.component.scss'],
    standalone: false
})
export class DynRawDetailCampaignsComponent implements OnInit {

  @Input() tenant: string;
  @Input() partner: string;
  @Input() isPersonalDataview: boolean = false;
  @Input() isCommandDataview: boolean = false;
  @Input() mainId: number; // personalId for contact fiche, commandId for command fiche
  @Input() email: string | undefined;
  campaigns: PersonalCampaignAssociation[] = [];
  loading = false;

  constructor(
    private campaignAssociationService: EngineCampaignAssociationService,
    private messageService: MessageService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    if (!this.tenant || !this.mainId) {
      return;
    }
    this.loadCampaigns();
  }

  loadCampaigns(): void {
    this.loading = true;

    const observable = this.isPersonalDataview
      ? this.campaignAssociationService.getByPersonal(this.tenant, this.mainId, this.partner)
      : this.campaignAssociationService.getByCommand(this.tenant, this.mainId, this.partner);

    observable.subscribe({
      next: campaigns => {
        // Normalize status to translated labels like in campaign list
        this.campaigns = (campaigns || []).map(c => ({
          ...c,
          status: this.getStatusLabel(c.status)
        }));
        this.loading = false;
      },
      error: err => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('view.details.campaigns.error.summary'),
          detail: this.translate.instant('view.details.campaigns.error.detail', { message: err.message })
        });
      }
    });
  }

  isImport(c: PersonalCampaignAssociation): boolean {
    return c.flux === 'IMPORT';
  }

  isExport(c: PersonalCampaignAssociation): boolean {
    return c.flux === 'EXPORT_SCENARIO' || c.flux === 'EXPORT_DIRECT';
  }

  isAwaitingTerminationCost(c: PersonalCampaignAssociation): boolean {
    return c.costAllocationStatus === 'AWAITING_TERMINATION';
  }

  costContactAmount(c: PersonalCampaignAssociation): number | null {
    if (this.isAwaitingTerminationCost(c)) {
      return null;
    }
    return this.coerceAmount(c.costContact);
  }

  costOrderAmount(c: PersonalCampaignAssociation): number | null {
    if (this.isAwaitingTerminationCost(c)) {
      return null;
    }
    return this.coerceAmount(c.costOrder);
  }

  hasNoCostAmounts(c: PersonalCampaignAssociation): boolean {
    return this.costContactAmount(c) == null && this.costOrderAmount(c) == null;
  }

  private coerceAmount(v: number | string | undefined | null): number | null {
    if (v == null || v === '') {
      return null;
    }
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }

  private getStatusLabel(status: string | undefined): string | undefined {
    if (!status) {
      return status;
    }
    const key = `campaign.status.${status}`;
    const translated = this.translate.instant(key);
    // si la clé n'existe pas, on retourne le code brut
    return translated === key ? status : translated;
  }

  getOriginLabel(origin: string | undefined): string | undefined {
    if (!origin) {
      return origin;
    }
    const key = `campaign.associationStatus.${origin}`;
    const translated = this.translate.instant(key);
    return translated === key ? origin : translated;
  }

  openCampaign(campaign: PersonalCampaignAssociation): void {
    if (!campaign || !campaign.campaignId) {
      return;
    }
    // Reuse the same routing pattern as the campaign listing/detail:
    // ['/h/marketing/campaign/', tenant, campaign.id]
    const url = `/h/marketing/campaign/${this.tenant}/${campaign.campaignId}`;
    window.open(url, '_blank');
  }

  openCommand(campaign: PersonalCampaignAssociation): void {
    if (!campaign || !campaign.commandRef || !this.tenant) {
      return;
    }
    const url =
      `/h/view/command?ref=${encodeURIComponent(campaign.commandRef)}` +
      `&tenant=${encodeURIComponent(this.tenant)}` +
      `&partner=${encodeURIComponent(this.partner || '')}` +
      `&code=command&detail=false`;
    window.open(url, '_blank');
  }
}

